import React, { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import { API_URL } from "../config";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  
  // Verificar si el usuario ya está autenticado al cargar la aplicación
  useEffect(() => {
    const checkUserLoggedIn = async () => {
      const token = localStorage.getItem('authToken');
      
      if (token) {
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const response = await axios.get(`${API_URL}/api/auth/user/`);
          setCurrentUser(response.data);
        } catch (err) {
          console.error('Error verificando usuario', err);
          localStorage.removeItem('authToken');
          delete axios.defaults.headers.common['Authorization'];
        }
      }
      
      setLoading(false);
    };
    
    checkUserLoggedIn();
  }, []);
  
  // Función para iniciar sesión
  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API_URL}/api/auth/login/`, {
        username: email,  // Using email as username
        password
      });
      
      const { access, refresh } = response.data;
      
      localStorage.setItem('authToken', access);
      localStorage.setItem('refreshToken', refresh);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access}`;
      
      // Get user data after successful login
      try {
        const userResponse = await axios.get(`${API_URL}/api/auth/user/`);
        setCurrentUser(userResponse.data);
      } catch (userErr) {
        console.error('Error fetching user data:', userErr);
        // Even if user data fetch fails, we're still logged in
      }
      
      navigate('/app/dashboard');
      return true;
    } catch (err) {
      console.error('Login error:', err.response?.data || err.message);
      setError(err.response?.data?.detail || 'Error al iniciar sesión');
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  // Función para registrarse
  const register = async (userData) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API_URL}/api/auth/register/`, userData);
      
      const { token, user } = response.data;
      
      localStorage.setItem('authToken', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setCurrentUser(user);
      navigate('/dashboard');
      
      return true;
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al registrarse');
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  // Función para cerrar sesión
  const logout = () => {
    localStorage.removeItem('authToken');
    delete axios.defaults.headers.common['Authorization'];
    setCurrentUser(null);
    navigate('/');
  };
  
  // Function to refresh the current user from the backend
  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/user/`);
      setCurrentUser(response.data);
    } catch (err) {
      console.error('Error refreshing user:', err);
      // Optionally handle error
    }
  };
  
  // Valores que expondrá el contexto
  const contextData = {
    currentUser,
    loading,
    error,
    login,
    register,
    logout,
    refreshUser
  };
  
  return (
    <AuthContext.Provider value={contextData}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 